import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { ArrowLeft, Plus, Layers, BookOpen, CheckSquare } from 'lucide-react';
import { DndProvider, useDrag } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { addEpic, addStory } from '../store';
import { Epic, Story, Task, Project } from '../types';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { generateId } from '../utils/idGenerator';

import { Badge } from '../components/ui/badge';
import SurfaceCard from '../components/SurfaceCard';
import { getStatusColors } from '../constants/colorScheme';
import {
  WIDGET_ALERT_INFO_CLASS,
  WIDGET_ALERT_INFO_TEXT_CLASS,
  WIDGET_ALERT_PURPLE_CLASS,
  WIDGET_ALERT_PURPLE_TEXT_CLASS,
  WIDGET_ALERT_SUCCESS_CLASS,
  WIDGET_ALERT_SUCCESS_TEXT_CLASS,
} from '../constants/widgetTheme';

const ITEM_TYPES = {
  EPIC: 'EPIC',
  STORY: 'STORY',
  TASK: 'TASK',
};

interface DraggableCardProps {
  type: 'epic' | 'story' | 'task';
  item: Epic | Story | Task;
  onClick: () => void;
}

const TYPE_CARD_STYLES = {
  epic: {
    panel: WIDGET_ALERT_PURPLE_CLASS,
    icon: WIDGET_ALERT_PURPLE_TEXT_CLASS,
  },
  story: {
    panel: WIDGET_ALERT_INFO_CLASS,
    icon: WIDGET_ALERT_INFO_TEXT_CLASS,
  },
  task: {
    panel: WIDGET_ALERT_SUCCESS_CLASS,
    icon: WIDGET_ALERT_SUCCESS_TEXT_CLASS,
  },
} as const;

const VIEW_BUTTON_ACTIVE_CLASS =
  'border border-primary bg-primary text-primary-foreground';
const VIEW_BUTTON_INACTIVE_CLASS =
  'border border-border bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground';
const TASK_ROW_CLASS =
  'cursor-pointer rounded border border-border bg-card p-3 transition-all hover:border-primary/40 hover:shadow-sm';

function DraggableCard({ type, item, onClick }: DraggableCardProps) {
  const [{ isDragging }, drag] = useDrag({
    type: type === 'epic' ? ITEM_TYPES.EPIC : type === 'story' ? ITEM_TYPES.STORY : ITEM_TYPES.TASK,
    item: { id: item.id, type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const tone = TYPE_CARD_STYLES[type];

  const getIcon = () => {
    if (type === 'epic') return <Layers className={`h-5 w-5 ${tone.icon}`} />;
    if (type === 'story') return <BookOpen className={`h-5 w-5 ${tone.icon}`} />;
    return <CheckSquare className={`h-4 w-4 ${tone.icon}`} />;
  };

  const getTitle = () => {
    if ('title' in item) return item.title;
    return '';
  };

  return (
    <div
      ref={drag}
      onClick={onClick}
      className={`cursor-move p-4 transition-shadow hover:shadow-md ${tone.panel} ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1">
          <p className="font-medium text-foreground">{getTitle()}</p>
          {'status' in item && (
            <Badge variant="outline" className={`mt-2 ${getStatusColors(item.status).badge}`}>
              {item.status}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PlanningBoard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [selectedView, setSelectedView] = useState<'hierarchy' | 'list'>('hierarchy');
  const { state, loading, reload } = useApp();
  const { projects, tasks, epics, projectIdSet } = useOrganizationData(state) as {
    projects: Project[];
    tasks: Task[];
    epics: Epic[];
    projectIdSet: Set<string>;
  };

  const project = useMemo(() => {
      return projects.find(
        (p: Project) => String(p.id).toLowerCase() === String(projectId).toLowerCase()
      );
    }, [projects, projectId]);

  if (loading || !state) {
    return <div className="p-8 text-center">Loading project...</div>;
  }
  
  const projectEpics = epics.filter((e) => e.projectId === projectId);
  const scopedStories = (state.stories || []).filter((story) => projectIdSet.has(story.projectId));
  const projectStories = scopedStories.filter((s) => s.projectId === projectId);
  const projectTasks = tasks.filter((t) => t.projectId === projectId);

  const refreshState = async () => {
    const email = state.currentUser?.email;
    if (email) {
      await reload(email);
    }
  };

  const handleCreateEpic = async () => {
    const organizationId =
      project?.organizationId || state.currentOrganization?.id || state.currentUser?.organizationId || '';

    const existingEpicIds = projectEpics.map((epic) => epic.id);
    const newEpic: Epic = {
      // id is auto-generated by Firestore; can be overridden by user
      documentId: generateId('doc', projectId),
      organizationId,
      projectId: projectId!,
      title: 'New Epic',
      description: '',
      status: 'Active',
      taskIds: [],
      standupNotes: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await addEpic(newEpic);
    await refreshState();
  };

  const handleCreateStory = async () => {
    const organizationId =
      project?.organizationId || state.currentOrganization?.id || state.currentUser?.organizationId || '';

    const newStory: Story = {
      id: generateId('story', projectId),
      organizationId,
      projectId: projectId!,
      title: 'New Story',
      description: '',
      status: 'Active',
      taskIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await addStory(newStory);
    await refreshState();
  };

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/app/projects/${projectId}`}
            className="mb-4 inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Project
          </Link>

          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <h1 className="mb-2 text-3xl font-semibold text-foreground">Planning Board</h1>
              <p className="text-muted-foreground">{project.name}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreateEpic}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
                New Epic
              </button>
              <button
                onClick={handleCreateStory}
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-card-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <Plus className="w-4 h-4" />
                New Story
              </button>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setSelectedView('hierarchy')}
            className={`rounded-lg px-4 py-2 transition-colors ${
              selectedView === 'hierarchy'
                ? VIEW_BUTTON_ACTIVE_CLASS
                : VIEW_BUTTON_INACTIVE_CLASS
            }`}
          >
            Hierarchy View
          </button>
          <button
            onClick={() => setSelectedView('list')}
            className={`rounded-lg px-4 py-2 transition-colors ${
              selectedView === 'list'
                ? VIEW_BUTTON_ACTIVE_CLASS
                : VIEW_BUTTON_INACTIVE_CLASS
            }`}
          >
            List View
          </button>
        </div>

        {/* Content */}
        {selectedView === 'hierarchy' ? (
          <div className="space-y-6">
            {projectEpics.map((epic) => {
              const epicStories = projectStories.filter((s) => s.epicId === epic.id);
              const epicTasks = projectTasks.filter((t) => t.epicId === epic.id);

              return (
                <div key={epic.id} className={`${WIDGET_ALERT_PURPLE_CLASS} p-6`}>
                  <div className="flex items-start gap-4 mb-4">
                    <Layers className={`mt-1 h-6 w-6 ${WIDGET_ALERT_PURPLE_TEXT_CLASS}`} />
                    <div className="flex-1">
                      <h3 className="mb-1 text-xl font-semibold text-foreground">{epic.title}</h3>
                      <p className="text-sm text-muted-foreground">{epic.description || 'No description yet'}</p>
                      <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{epicStories.length} Stories</span>
                        <span>{epicTasks.length} Tasks</span>
                        <Badge variant="outline" className={getStatusColors(epic.status).badge}>
                          {epic.status}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Stories in Epic */}
                  {epicStories.length > 0 && (
                    <div className="ml-10 space-y-4">
                      {epicStories.map((story) => {
                        const storyTasks = projectTasks.filter((t) => t.storyId === story.id);
                        return (
                          <div
                            key={story.id}
                            className={`${WIDGET_ALERT_INFO_CLASS} p-4`}
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <BookOpen className={`mt-1 h-5 w-5 ${WIDGET_ALERT_INFO_TEXT_CLASS}`} />
                              <div className="flex-1">
                                <h4 className="font-medium text-foreground">{story.title}</h4>
                                <p className="text-sm text-muted-foreground">{story.description || 'No description yet'}</p>
                                <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
                                  <span>{storyTasks.length} Tasks</span>
                                  <Badge variant="outline" className={getStatusColors(story.status).badge}>
                                    {story.status}
                                  </Badge>
                                </div>
                              </div>
                            </div>

                            {/* Tasks in Story */}
                            {storyTasks.length > 0 && (
                              <div className="ml-8 space-y-2">
                                {storyTasks.map((task) => (
                                  <div
                                    key={task.id}
                                    onClick={() => navigate(`/task/${task.id}`)}
                                    className={`${TASK_ROW_CLASS} flex items-start gap-2`}
                                  >
                                    <CheckSquare className={`mt-0.5 h-4 w-4 ${WIDGET_ALERT_SUCCESS_TEXT_CLASS}`} />
                                    <div className="flex-1 text-sm">
                                      <p className="font-medium text-foreground">{task.title}</p>
                                      <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                                        <span>{task.status}</span>
                                        <span>{task.estimatedDays}d</span>
                                        <span className="text-muted-foreground/70">•</span>
                                        <span>{task.priority}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Tasks directly in Epic (no story) */}
                  {epicTasks.filter((t) => !t.storyId).length > 0 && (
                    <div className="ml-10 mt-4 space-y-2">
                      <p className="mb-2 text-sm text-muted-foreground">Direct Tasks:</p>
                      {epicTasks
                        .filter((t) => !t.storyId)
                        .map((task) => (
                          <div
                            key={task.id}
                            onClick={() => navigate(`/task/${task.id}`)}
                            className={`${TASK_ROW_CLASS} flex items-start gap-2`}
                          >
                            <CheckSquare className={`mt-0.5 h-4 w-4 ${WIDGET_ALERT_SUCCESS_TEXT_CLASS}`} />
                            <div className="flex-1 text-sm">
                              <p className="font-medium text-foreground">{task.title}</p>
                              <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                                <span>{task.status}</span>
                                <span>{task.estimatedDays}d</span>
                                <span className="text-muted-foreground/70">•</span>
                                <span>{task.priority}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Stories without Epics */}
            {projectStories.filter((s) => !s.epicId).length > 0 && (
              <div className={`${WIDGET_ALERT_INFO_CLASS} p-6`}>
                <h3 className="mb-4 text-lg font-semibold text-foreground">Stories without Epic</h3>
                <div className="space-y-4">
                  {projectStories
                    .filter((s) => !s.epicId)
                    .map((story) => {
                      const storyTasks = projectTasks.filter((t) => t.storyId === story.id);
                      return (
                        <SurfaceCard key={story.id} className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <BookOpen className={`mt-1 h-5 w-5 ${WIDGET_ALERT_INFO_TEXT_CLASS}`} />
                            <div className="flex-1">
                              <h4 className="font-medium text-foreground">{story.title}</h4>
                              <p className="text-sm text-muted-foreground">{story.description || 'No description yet'}</p>
                              <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
                                <span>{storyTasks.length} Tasks</span>
                                <Badge variant="outline" className={getStatusColors(story.status).badge}>
                                  {story.status}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          {storyTasks.length > 0 && (
                            <div className="ml-8 space-y-2">
                              {storyTasks.map((task) => (
                                <div
                                  key={task.id}
                                  className={`${TASK_ROW_CLASS} flex items-start gap-2`}
                                >
                                  <CheckSquare className={`mt-0.5 h-4 w-4 ${WIDGET_ALERT_SUCCESS_TEXT_CLASS}`} />
                                  <div className="flex-1 text-sm">
                                    <p className="font-medium text-foreground">{task.title}</p>
                                    <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                                      <span>{task.status}</span>
                                      <span>{task.estimatedDays}d</span>
                                      <span className="text-muted-foreground/70">•</span>
                                      <span>{task.priority}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </SurfaceCard>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Tasks without Epic or Story */}
            {projectTasks.filter((t) => !t.epicId && !t.storyId).length > 0 && (
              <SurfaceCard className="p-6">
                <h3 className="mb-4 text-lg font-semibold text-foreground">Unassigned Tasks</h3>
                <div className="space-y-2">
                  {projectTasks
                    .filter((t) => !t.epicId && !t.storyId)
                    .map((task) => (
                      <div
                        key={task.id}
                        onClick={() => navigate(`/task/${task.id}`)}
                        className={`${TASK_ROW_CLASS} flex items-start gap-2`}
                      >
                        <CheckSquare className={`mt-0.5 h-4 w-4 ${WIDGET_ALERT_SUCCESS_TEXT_CLASS}`} />
                        <div className="flex-1 text-sm">
                          <p className="font-medium text-foreground">{task.title}</p>
                          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                            <span>{task.status}</span>
                            <span>{task.estimatedDays}d</span>
                            <span className="text-muted-foreground/70">•</span>
                            <span>{task.priority}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </SurfaceCard>
            )}
          </div>
        ) : (
          /* List View */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Epics Column */}
            <div>
              <h3 className="mb-4 flex items-center gap-2 text-lg text-foreground">
                <Layers className={`h-5 w-5 ${WIDGET_ALERT_PURPLE_TEXT_CLASS}`} />
                Epics ({projectEpics.length})
              </h3>
              <div className="space-y-3">
                {projectEpics.map((epic) => (
                  <DraggableCard key={epic.id} type="epic" item={epic} onClick={() => {}} />
                ))}
              </div>
            </div>

            {/* Stories Column */}
            <div>
              <h3 className="mb-4 flex items-center gap-2 text-lg text-foreground">
                <BookOpen className={`h-5 w-5 ${WIDGET_ALERT_INFO_TEXT_CLASS}`} />
                Stories ({projectStories.length})
              </h3>
              <div className="space-y-3">
                {projectStories.map((story) => (
                  <DraggableCard key={story.id} type="story" item={story} onClick={() => {}} />
                ))}
              </div>
            </div>

            {/* Tasks Column */}
            <div>
              <h3 className="mb-4 flex items-center gap-2 text-lg text-foreground">
                <CheckSquare className={`h-5 w-5 ${WIDGET_ALERT_SUCCESS_TEXT_CLASS}`} />
                Tasks ({projectTasks.length})
              </h3>
              <div className="space-y-3">
                {projectTasks.slice(0, 20).map((task) => (
                  <DraggableCard 
                    key={task.id} 
                    type="task" 
                    item={task} 
                    onClick={() => navigate(`/task/${task.id}`)} 
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {projectEpics.length === 0 && projectStories.length === 0 && projectTasks.length === 0 && (
          <SurfaceCard className="p-12 text-center">
            <p className="mb-4 text-muted-foreground">No planning items yet</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleCreateEpic}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
                Create Epic
              </button>
              <button
                onClick={handleCreateStory}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-card-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <Plus className="w-4 h-4" />
                Create Story
              </button>
            </div>
          </SurfaceCard>
        )}
      </div>
    </DndProvider>
  );
}